import React, { memo, useEffect, useState } from 'react'
import { ISelectOption } from 'components/forms/Selects'
import moment from 'moment-timezone'
import { DriverName } from 'apps/fleet-planner/components/drivers/DriverName'
import { DriverStatusTag } from 'apps/fleet-planner/components/drivers/DriverStatusTag'
import { DriverStatusType, IDriver } from 'apps/fleet-planner/types'
import { TableRow, TableCell } from 'transfix-ui'
import { isToday } from 'utils/appointmentHelpers'
import { formatPhone } from 'utils/formatPhone'
import { FORMATS } from 'constants/datetime'
import { DISPLAY } from 'constants/display'

interface IDriversTableRow {
  driver: IDriver
  isUpdated?: boolean
  onEditRow: (d: IDriver) => void
  onStatusUpdate: (driver: Partial<IDriver>) => void
}

const updatedAtTime = (timeStamp: string) => {
  if (!timeStamp) return DISPLAY.empty
  const dateTime = moment(timeStamp).format(FORMATS.monthDayTimeFormatShort12h)
  const time = moment(timeStamp).format(FORMATS.timeFormatShort12H)
  return isToday(time) ? `Today, ${time}` : dateTime
}

export const DriversTableRow = memo<IDriversTableRow>(
  ({ driver, isUpdated, onEditRow, onStatusUpdate }) => {
    const { cellPhone = '', email = '', motorCarrierLegalName = '', updatedAt = '' } = driver || {}
    const [hasHighlight, setHasHighlight] = useState<boolean>(false)

    // if isUpdated is toggled, we want to highlight the row
    // but only do it once per mount
    useEffect(() => {
      if (isUpdated) {
        setHasHighlight(true)
        const timer = setTimeout(() => setHasHighlight(false), 3000)
        return () => clearTimeout(timer)
      }
      return undefined
    }, [])

    const rowClickHandler = () => {
      onEditRow(driver)
    }

    const statusSelectHandler = async (selectedStatus: ISelectOption) => {
      const { value } = selectedStatus || {}
      const req = {
        ...driver,
        motorCarrier: {
          id: driver.motorCarrierId ?? 0,
          name: driver.motorCarrierLegalName ?? '',
        },
        status: value as DriverStatusType,
      }
      onStatusUpdate(req)
    }

    return (
      <TableRow
        dataTest='driver-row'
        className={`${hasHighlight ? 'fp-loads-list-row-updated' : ''} fp-loads-list-row`}
        onClick={rowClickHandler}>
        <TableCell className='pl6' dataTest={`list-row-driver-status-${driver?.id}`}>
          <DriverStatusTag driverStatus={driver.status!} onSelectHandler={statusSelectHandler} />
        </TableCell>
        <TableCell dataTest={`list-row-driver-name-${driver?.id}`}>
          <DriverName driver={driver} />
        </TableCell>
        <TableCell dataTest={`list-row-carrier-name-${driver?.id}`}>
          {motorCarrierLegalName || DISPLAY.empty}
        </TableCell>
        <TableCell className='fs-mask' dataTest={`list-row-cellphone-${driver?.id}`}>
          {formatPhone(cellPhone, { removeCountryCode: '+1' }) || DISPLAY.empty}
        </TableCell>
        <TableCell className='fs-mask' dataTest={`list-row-email-${driver?.id}`}>
          {email || DISPLAY.empty}
        </TableCell>
        <TableCell dataTest={`list-row-updated-at-${driver?.id}`}>
          {updatedAtTime(updatedAt)}
        </TableCell>
      </TableRow>
    )
  }
)
